import { queryKeys } from '@query/keys'
import { useQuery } from '@tanstack/react-query'
import classNames from 'classnames'
import React, { FC, useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'

import { Navigate } from '@lib/react-router-dom'

import { useDocuments } from '@pages/Profile/DocumentManagement/DocumentManagementPage'
import {
  getFinancialInfoNextRoute,
  useFinancialInfoNextRoute,
} from '@pages/Signup/hooks/useFinancialInfoNextRoute'

import { useAccountReadContext, useAccountWriteContext } from '@context/AccountContextContext'
import { useSessionEntity } from '@context/EntityContext'
import { useProductReadContext } from '@context/ProductContext'
import { useSessionLanguage } from '@context/SessionSettingsContext'

import { Body1, Heading3 } from '@ui/Typography'
import { Button } from '@ui/components/Button'

import { hasSubmittedEconomicProfileTest } from '@utils/AccountAccess/accountProductStatuses'
import { useApiClient } from '@utils/ApiClient'
import { useGAEvents } from '@utils/callGAEvents'
import { ClientApiClient } from '@utils/clientApi'
import {
  TickmillCompaniesEnum,
  isTickmillEU,
  isTickmillUK,
  requiresAppTestImprovement,
} from '@utils/companyName.utils'
import { useWindowResize } from '@utils/domUtils'
import { BasePaths } from '@utils/path'
import { isOne, isZero } from '@utils/validations'

import { isAccountAppropriatenessTestSubmittedStatus } from '@model/AccountDetailedStatus'
import { AccountType } from '@model/AccountType'
import { TestAnswerOutcomeStatus, TestAnswersDto } from '@model/ClientTestAnswerDto'
import { DocumentCategoryType } from '@model/DocumentCategories'
import { DocumentCategoryOptionType } from '@model/DocumentCategoryOptionType'
import { TestFormValues } from '@model/Test'
import {
  AppropriatenessTestSectionCode,
  TestSectionDto,
  isAppropriatenessTestPersonalInformation,
} from '@model/TestSectionsDto'

import { useClientTestSubmission } from '@hooks/useClientTestSubmission'
import { scrollToTop } from '@hooks/useScrollToTop'

import { Loading } from '@shared/components/Loading/Loading'
import { TestForm, TestFormRef } from '@shared/templates/Test/TestForm'

import { LeftSection } from '../../Components/LeftSection'
import { SignupFormWrapper } from '../../Components/SignupFormWrapper'
import { SignupSections, SignupStep } from '../../Components/SignupStep'
import { SignupWrapper } from '../../Components/SignupWrapper'

import styles from './FinancialInfoPage.module.scss'

enum FailReason {
  Test = 'test',
  Signup = 'signup',
}

const useAppropriatenessTestMutate = (clientId?: string) => {
  const apiClient = useApiClient(ClientApiClient)
  const locale = useSessionLanguage()
  const { product } = useProductReadContext()
  const { account } = useAccountReadContext()
  const entity = useSessionEntity()

  const [isSuccess, setSuccess] = useState(false)
  const [isSignupFail, setSignupFail] = useState(false)
  const [isTestFail, setTestFail] = useState(false)
  const [accountApTestAnswers, setAccountApTestAnswers] = useState<TestAnswersDto | undefined>()
  const [hasCheckedSubmittedStatus, setHasCheckedSubmittedStatus] = useState(false)
  const [nextRoute, setNextRoute] = useState<string | undefined>()

  const { refreshAccount } = useAccountWriteContext()
  const { callGAEvents } = useGAEvents()

  const { performSubmission, submitTest, uploadDocuments, isLoading } = useClientTestSubmission()

  const resolveSuccessfulTestRoute = useCallback(async () => {
    const refreshedAccount = await refreshAccount(locale)
    const route = getFinancialInfoNextRoute({
      account: refreshedAccount ?? account,
      product,
      isLoadingTest: false,
      isSuccess: true,
      isSignupFail: false,
      isTestFail: false,
    })

    setNextRoute(route)
    setSuccess(true)
  }, [account, locale, product, refreshAccount])

  const handleTestAnswers = useCallback(
    async (answers: TestAnswersDto) => {
      setAccountApTestAnswers(answers)

      if (answers.isClientVulnerable) {
        setSignupFail(true)
      } else if (answers.outcome?.outcome === TestAnswerOutcomeStatus.FAILED) {
        setTestFail(true)
      } else {
        await resolveSuccessfulTestRoute()
      }
    },
    [resolveSuccessfulTestRoute]
  )

  const mutate = async (values: TestFormValues) => {
    await performSubmission(async () => {
      await submitTest(values, {
        clientId: clientId || '',
        category: values.category,
        testId: values.testId,
      })

      const answers = await apiClient.getAccountAppTestAnswers(locale, product)

      if (account?.type.id !== AccountType.Corporate) {
        callGAEvents({
          entity,
          kycVerificationProvider: account?.visibilityConfiguration?.kycVerificationProvider,
          onboardingFlowType: account?.visibilityConfiguration?.onboardingFlowType.toString(),
          products: account?.tickmillProducts.toString(),
        }).Onboarding.AppTest.submit()
      }

      await uploadDocuments(values, {
        categoryId: DocumentCategoryType.AppropriatenessTest,
        typeId: DocumentCategoryOptionType.TradingStatement,
        ticketIdOrTestId: answers.id,
      })

      await handleTestAnswers(answers)
      scrollToTop()
    })
  }

  useEffect(() => {
    setHasCheckedSubmittedStatus(false)
  }, [account?.id, account?.status.id, product])

  useEffect(() => {
    if (!isAccountAppropriatenessTestSubmittedStatus(account?.status.id)) return
    if (accountApTestAnswers) return
    if (hasCheckedSubmittedStatus) return

    let isMounted = true

    const checkAccountTestStatus = async () => {
      try {
        const answers = await apiClient.getAccountAppTestAnswers(locale, product)
        await handleTestAnswers(answers)
      } catch (error: unknown) {
        console.error('Error fetching test answers', error)
      } finally {
        if (isMounted) {
          setHasCheckedSubmittedStatus(true)
        }
      }
    }

    checkAccountTestStatus()

    return () => {
      isMounted = false
    }
  }, [
    account?.status.id,
    accountApTestAnswers,
    apiClient,
    handleTestAnswers,
    hasCheckedSubmittedStatus,
    locale,
    product,
  ])

  return {
    mutate,
    isLoading,
    isSuccess,
    isSignupFail,
    isTestFail,
    accountApTestAnswers,
    hasCheckedSubmittedStatus,
    nextRoute,
  }
}

export const FinancialInfoPage: React.FC = () => {
  const { clientId } = useParams<{ clientId?: string }>()
  const childRef = useRef<TestFormRef>(null)
  const { t } = useTranslation()
  const apiClient = useApiClient(ClientApiClient)
  const entity = useSessionEntity()
  const isMobile = useWindowResize()
  const { account } = useAccountReadContext()
  const { refreshAccount } = useAccountWriteContext()
  const locale = useSessionLanguage()

  const [formValues, setFormValues] = useState<TestFormValues>()
  const [step, setStep] = useState('')

  const { accountApTestAnswers, hasCheckedSubmittedStatus, ...mutation } =
    useAppropriatenessTestMutate(clientId)

  const { data, isLoading: isLoadingTest } = useQuery({
    queryKey: queryKeys.signup.financialInfoTest(locale),
    queryFn: () => apiClient.getClientAppTests(locale),
  })
  const documentsProps = useDocuments()
  const documents = documentsProps.data || []
  const { product } = useProductReadContext()
  const sections = data?.sections || []
  const stepIndex = Math.max(
    sections.findIndex((x) => x.code === step),
    0
  )
  const subStep = stepIndex + 1
  const sectionSize = sections.length

  const [isFirstStep, setIsFirstStep] = useState(true)
  const entityRequiresAppTestImprovement = requiresAppTestImprovement(entity)
  const isAppTestImprovementRequired = entityRequiresAppTestImprovement && isFirstStep
  const isEconomicProfileTest = hasSubmittedEconomicProfileTest(account, product)
  const sectionStep = isEconomicProfileTest
    ? SignupSections.EconomicProfile
    : SignupSections.FinancialInfo

  useEffect(() => {
    window.onpopstate = () => refreshAccount(locale)
  }, [locale, refreshAccount])

  useEffect(() => {
    const sections = data?.sections || []
    if (!sections.length) return
    if (sections.some((section) => section.code === step)) return

    const [section] = sections
    setStep(section.code)
  }, [data?.sections, step])

  const handleSubmit = async (values: TestFormValues) => {
    await mutation.mutate(values)
  }

  const handleNextStep = (values: TestFormValues) => {
    const sections = data?.sections || []
    const stepIndex = sections.findIndex((x) => x.code === step)
    const section = sections?.[stepIndex + 1]
    if (section?.code) {
      setStep(section.code)
      setFormValues(values)
      scrollToTop()
    }
  }

  const handlePrevStep = (values: TestFormValues) => {
    const sections = data?.sections || []
    const stepIndex = sections.findIndex((x) => x.code === step)
    const section = sections?.[stepIndex - 1]
    if (section?.code) {
      setStep(section.code)
      setFormValues(values)
      scrollToTop()
    }
  }

  const isSubmittedStatusPendingRoute =
    isAccountAppropriatenessTestSubmittedStatus(account?.status.id) &&
    !accountApTestAnswers &&
    !hasCheckedSubmittedStatus &&
    !mutation.isSuccess &&
    !mutation.isSignupFail &&
    !mutation.isTestFail

  const completedRoute = useFinancialInfoNextRoute({
    isLoadingTest,
    isSuccess: false,
    isSignupFail: false,
    isTestFail: false,
  })

  if (mutation.nextRoute) {
    return <Navigate to={mutation.nextRoute} replace />
  }

  const section = data?.sections?.find((x) => x.code === step)

  if (mutation.isSignupFail) {
    return <Navigate to={`fail/${FailReason.Signup}`} replace />
  }

  if (mutation.isTestFail) {
    return <Navigate to={`fail/${FailReason.Test}`} replace />
  }

  if (!isSubmittedStatusPendingRoute && completedRoute) {
    return <Navigate to={completedRoute} replace />
  }

  const renderCustomContent = (currentSection: TestSectionDto) => {
    return (
      <>
        {!isOne(stepIndex + 1) && isMobile && entityRequiresAppTestImprovement && (
          <AlignmentMessage sectionCode={currentSection.code} />
        )}
        {!entityRequiresAppTestImprovement &&
          isAppropriatenessTestPersonalInformation(currentSection.code) && (
            <Body1>
              {t(
                "Sign up.Here we'll need some information about your education and current employer."
              )}
            </Body1>
          )}
      </>
    )
  }

  return (
    <SignupFormWrapper
      label={t('Sign up.Sign Up')}
      onBackToPreviousStep={!isZero(stepIndex) ? () => childRef?.current?.goPrevStep() : undefined}
      cancelPath={BasePaths.TakeAppTest}
    >
      <SignupStep
        accountTypeId={account?.type.id}
        subStep={{
          current: stepIndex <= sectionSize ? subStep : sectionSize,
          total: sectionSize,
        }}
        section={sectionStep}
        isHidden={isAppTestImprovementRequired}
      />
      <Loading showLoadingIcon isLoading={isLoadingTest || isSubmittedStatusPendingRoute}>
        <SignupWrapper noBackground={subStep === 1 && !isFirstStep}>
          {entityRequiresAppTestImprovement && !isFirstStep && (isOne(subStep) || !isMobile) && (
            <LeftSection
              hideInfo
              title={
                isTickmillEU(account) || isTickmillUK(account)
                  ? t('Sign up.Appropriateness Test')
                  : t('Sign up.Financial Info & Experience')
              }
              description={() => (
                <React.Fragment>
                  {account?.type.id === AccountType.Individual && (
                    <Body1 isParagraph medium>
                      {t(
                        'Sign up.Please answer all questions with accurate information that reflects your current situation, knowledge, and experience'
                      )}
                    </Body1>
                  )}
                  {account?.type.id === AccountType.Corporate &&
                    entity !== TickmillCompaniesEnum.TICKMILL_UK && (
                      <Body1 isParagraph medium>
                        {t(
                          'Sign up.Please fill these sections in accordance to the knowledge and experience of the person authorised to trade on behalf of the company.'
                        )}
                      </Body1>
                    )}
                  {isOne(subStep) && (
                    <Body1 isParagraph>
                      {t(
                        'Sign up.We will not contact any employer you have specified under any circumstances'
                      )}
                    </Body1>
                  )}
                  {!isOne(subStep) && <AlignmentMessage sectionCode={section?.code || ''} />}
                </React.Fragment>
              )}
            />
          )}

          {!entityRequiresAppTestImprovement && (
            <LeftSection
              title={t('Sign up.Financial Info & Experience')}
              description={() => (
                <React.Fragment>
                  {account?.type.id === AccountType.Individual && (
                    <Body1 isParagraph>
                      {t(
                        'Sign up.Please fill these sections in accordance to your knowledge and experience'
                      )}
                    </Body1>
                  )}
                  {account?.type.id === AccountType.Corporate && (
                    <Body1 isParagraph>
                      {t(
                        'Sign up.Please fill these sections in accordance to the knowledge and experience of the person authorised to trade on behalf of the company.'
                      )}
                    </Body1>
                  )}
                </React.Fragment>
              )}
            />
          )}
          <section
            className={classNames(styles.testWrapper, {
              [styles.formSection]: !isAppTestImprovementRequired,
            })}
          >
            {isAppTestImprovementRequired ? (
              <div
                className={classNames(styles.infoSection, {
                  [styles.testInfo]: isAppTestImprovementRequired,
                })}
              >
                <Heading3>{t('Sign up.Appropriateness Test')}</Heading3>
                <ul className={styles.list}>
                  {[
                    t('Sign up.Answer all questions with accurate information'),
                    t(
                      'Sign up.Completion and passing of the appropriateness test are prerequisites'
                    ),
                    t('Sign up.We will evaluate your trading knowledge, trading experience'),
                  ].map((text, index) => (
                    <li key={index} className='is-flex'>
                      <div className={styles.bullet} />
                      <div>
                        <Body1>{text}</Body1>
                      </div>
                    </li>
                  ))}
                </ul>
                <div className={styles.button}>
                  <Button.Primary
                    size='lg'
                    onClick={() => {
                      setIsFirstStep(false)
                      scrollToTop()
                    }}
                  >
                    {t('Sign up.Start Test')}
                  </Button.Primary>
                </div>
              </div>
            ) : (
              !!data && (
                <TestForm
                  ref={childRef}
                  data={data}
                  step={step}
                  values={formValues}
                  onSubmit={handleSubmit}
                  goPrevStep={handlePrevStep}
                  goNextStep={handleNextStep}
                  documents={documents}
                  renderBeforeQuestions={renderCustomContent}
                />
              )
            )}
          </section>
        </SignupWrapper>
      </Loading>
    </SignupFormWrapper>
  )
}

export const AlignmentMessage: FC<{ sectionCode?: string }> = ({ sectionCode }) => {
  const { t } = useTranslation()

  const AlignmentMessage: Record<string, string> = {
    [AppropriatenessTestSectionCode.FinancialBackground]: t(
      'Sign up.To align our products with your financial profile as part of our KYC process, we need details about your financial background'
    ),
    [AppropriatenessTestSectionCode.TradingKnowledge]: t(
      'Sign up.To align our products with your financial profile as part of our KYC process, we need details about your trading knowledge and experience'
    ),
    [AppropriatenessTestSectionCode.CFDInvestmentKnowledge]: t(
      'Sign up.To align our products with your financial profile as part of our KYC process, we need details about your Investment Knowledge'
    ),
    [AppropriatenessTestSectionCode.ETDInvestmentKnowledge]: t(
      'Sign up.To align our products with your financial profile as part of our KYC process, we need details about your Investment Knowledge of leverage products'
    ),
  }

  if (!sectionCode || !AlignmentMessage[sectionCode]) {
    return null
  }

  return <Body1>{AlignmentMessage[sectionCode]}</Body1>
}
